// ── <ShareDialog> — the owner's share control for one forecast ─────────────
//
// Pure props (components/ convention): every network call lives in
// hooks/useForecastShares.js (this forecast's current shares + save) and
// hooks/useShareCandidates.js (the picker's team/people lists) and reaches this
// component as props. The dialog's OWN local state is the in-progress EDIT —
// checkbox clicks not yet saved. The caller (App.jsx) mounts this component only
// while `open` is true, so a fresh mount is a fresh edit session with no stale
// local state to reset by hand.
//
// ADDITIVE CHECKBOXES, ONE RULE (collaborative-forecasts design). The three
// lanes — the whole organization, teams, named people — used to be a
// mutually-exclusive MODE, rendered as a radio group, with only the selected
// lane's controls on screen. They are now independent checkboxes, all visible at
// once, under a single rule the owner can read off the screen:
//
//     A CHECKED BOX MEANS ACCESS. AN UNCHECKED BOX MEANS NONE.
//
// ⭐ WHY THE OLD PRESERVATION RULE IS GONE, AND WHAT REPLACED IT. `PUT` replaces
// the ENTIRE share set — anything absent from the submitted array is revoked. The
// dialog therefore used to fold any grant it could not DRAW silently back into
// every save ("an untouched lane is submitted as-loaded"), because an absent
// control cannot express intent. That rule directly contradicts the one above:
// an owner would uncheck everything, save, and still be sharing.
//
// So preservation is retired, and the hazard it existed for is closed the other
// way round — by making sure there is no such thing as a grant without a
// checkbox. `orphanGrants` (lib/shareDialog.js) finds the grants the candidate
// lists cannot offer (a team whose members have all left; a person who has left
// the org) and they are rendered as real, checked, labelled boxes. Nothing is
// dropped silently and nothing is kept silently: the owner sees every grant and
// decides. Do not reinstate invisible preservation on top of this — the two
// models cannot both be true, and the tests pin this one.
//
// LOCAL STATE IS STILL OVERRIDES ON A BASELINE, not an absolute selection seeded
// once at mount. `deriveInitialSelection` recomputes the baseline from props on
// every render and `useState` holds only the DELTA, so a box the user never
// touched reflects whatever `shares` currently says — with no async race between
// "props arrived" and "state was seeded from empty props".
//
// A LOUD REFUSAL, NEVER A QUIET LOSS. Save stays disabled while any existing
// individual share cannot be resolved to an identity we could submit back
// (`unresolvedUserShares`). That is the one case an orphan checkbox cannot
// cover: without a `userId` there is nothing to send.
//
// THE COUNT IS THE DISCLOSURE, NEVER A ROSTER. A team row shows how many members
// it has; it never lists who they are.
//
// LAYOUT. A "PEOPLE AND TEAMS" section — a search field whose results appear only as
// the owner types, and below it the teams and people who have direct access, each
// with REMOVE (the list scrolls past three rows, so the dialog cannot grow without
// bound) — then "GENERAL ACCESS": the organization and the link as two cards, still
// independent checkboxes (both can be on), the link card carrying the URL and a COPY
// LINK button. REMOVE and adding from search are the same toggles the checkboxes
// were: nothing is granted or revoked until UPDATE ACCESS.
import { useState } from "react";
import { C } from "../lib/colors.js";
import { ModalShell, CloseButton } from "./Panels.jsx";
import {
  candidateLabel,
  deriveInitialSelection,
  buildDesiredShares,
  orphanGrants,
  matchesQuery,
  copyForecastUrl,
  initialsOf,
} from "../lib/shareDialog.js";
import { ui } from "../lib/telemetry/instrument.js";
import { Button, Spinner } from "./ui/Primitives.jsx";
import { IconSearch } from "./ui/Icons.jsx";

const MONO = '"IBM Plex Mono",monospace';

// The dialog title: large, so it still reads as the dialog's name above the two
// smaller section headings.
const TITLE_STYLE = {
  fontSize: 18,
  color: C.white,
  fontWeight: 600,
};

// People and teams / General access.
const SECTION_STYLE = {
  fontSize: 15,
  fontWeight: 600,
  color: C.white,
};

const HINT_STYLE = { fontSize: 12, color: C.gray, lineHeight: 1.5 };

// Tall enough for exactly three rows; a fourth scrolls.
const ROW_HEIGHT = 64;
const MAX_VISIBLE_ROWS = 3;

function SearchIcon() {
  return (
    <span style={{ color: C.gray, display: "inline-flex", flexShrink: 0 }}>
      <IconSearch width={18} height={18} />
    </span>
  );
}

/** A one-line loading state: the shared spinner beside what is loading. */
function LoadingLine({ children, style }) {
  return (
    <div style={{ ...HINT_STYLE, display: "flex", alignItems: "center", gap: 8, ...style }}>
      <Spinner label={typeof children === "string" ? children : "Loading"} />
      {children}
    </div>
  );
}

function TeamIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ stroke: C.accent }}
      aria-hidden="true"
    >
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
      <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" />
      <path d="M18.5 14.4c1.8.8 3 2.8 3 5.6" />
    </svg>
  );
}

/** The square (team) or round (person) mark at the head of a row. */
function Avatar({ team, label }) {
  return (
    <div
      style={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: team ? 10 : "50%",
        background: C.btnBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: C.accent,
        fontFamily: MONO,
        fontSize: 14,
      }}
    >
      {team ? <TeamIcon /> : initialsOf(label)}
    </div>
  );
}

/** One team/person row: avatar, name, sub-line, and an optional trailing control. */
function EntryRow({ team, title, sub, trailing, rowProps, height }) {
  return (
    <div
      {...rowProps}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        minHeight: height,
        padding: "0 10px",
        boxSizing: "border-box",
      }}
    >
      <Avatar team={team} label={title} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 15,
            color: C.white,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </div>
        {sub && (
          <div
            style={{
              fontSize: 12,
              color: C.gray,
              marginTop: 3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {sub}
          </div>
        )}
      </div>
      {trailing}
    </div>
  );
}

const teamSub = (t) => `Team · ${t.memberCount} member${t.memberCount === 1 ? "" : "s"}`;
// A person's sub-line is their email when the title is their name.
const personSub = (p) => (p.name && p.email ? p.email : null);

/** A GENERAL ACCESS option: a bordered card around a checkbox, a title and a line.
 *  Any `data-*` prop is forwarded to the checkbox itself, `data-ui` / `data-ui-key` too. */
function AccessCard({
  checked,
  disabled,
  onChange,
  title,
  body,
  children,
  "data-ui": uiId,
  "data-ui-key": uiKey,
  ...dataProps
}) {
  return (
    <div
      style={{
        border: `1px solid ${checked ? C.accent : C.border}`,
        background: checked ? C.btnBg : "transparent",
        borderRadius: 10,
        padding: "16px 18px",
        marginTop: 12,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <label
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 14,
          cursor: disabled ? "default" : "pointer",
        }}
      >
        <input
          type="checkbox"
          {...dataProps}
          {...ui(uiId, uiKey)}
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          style={{
            width: 18,
            height: 18,
            margin: "2px 0 0",
            accentColor: C.accent,
            flexShrink: 0,
            cursor: disabled ? "default" : "pointer",
          }}
        />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 15, color: C.white }}>{title}</span>
          <span style={{ display: "block", ...HINT_STYLE, marginTop: 4 }}>{body}</span>
        </span>
      </label>
      {children}
    </div>
  );
}

/** A greyed "— no longer …" note on an orphaned grant. */
const orphanNote = (text) => <span style={{ color: C.gray }}>{text}</span>;

/**
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {string|null} [props.orgName] the caller's own org display name (`/api/me`'s `org.name`)
 * @param {string|null} [props.orgId] the caller's own org id — never read off a loaded share
 * @param {boolean} [props.isOwn] false for a forecast shared WITH the viewer; they get a
 *   notice instead of controls, because only the owner manages sharing.
 * @param {string|null} [props.forecastUrl] the shareable URL for this forecast — the one
 *   already in the owner's address bar. Absent disables the URL box rather than offering a
 *   control that would copy nothing.
 * @param {Function} [props.writeClipboard] injectable clipboard write; must answer `true`
 *   (or a promise of it) on success. Same contract as `useMintedLinkStash`.
 * @param {boolean} [props.loading]
 * @param {Array<object>} [props.shares] current grants (`GET`)
 * @param {boolean} [props.candidatesLoading]
 * @param {Array<object>} [props.teams] team candidates (current members only)
 * @param {Array<object>} [props.people] people candidates (current org members)
 * @param {boolean} [props.saving]
 * @param {string|null} [props.error]
 * @param {(desiredShares: Array<object>) => Promise<boolean>} props.onSave
 */
export default function ShareDialog({
  onClose,
  orgName = null,
  orgId = null,
  isOwn = true,
  forecastUrl = null,
  writeClipboard,
  loading = false,
  shares = [],
  candidatesLoading = false,
  teams = [],
  people = [],
  saving = false,
  error = null,
  onSave,
}) {
  // `null` in orgOverride means "follow the loaded baseline"; team/people
  // overrides are per-key so an untouched key ALSO follows the baseline.
  const [orgOverride, setOrgOverride] = useState(null);
  const [teamOverrides, setTeamOverrides] = useState({});
  const [peopleOverrides, setPeopleOverrides] = useState({});
  const [query, setQuery] = useState("");
  // The search results show only while the field has focus AND something is typed —
  // they populate as the owner searches, rather than listing everyone up front.
  const [searchFocused, setSearchFocused] = useState(false);
  const [linkOverride, setLinkOverride] = useState(null);
  // Unticking the URL box revokes everyone the link reached, so it asks first.
  // Ticking it does not — that direction is additive and undoable.
  const [confirmingLinkOff, setConfirmingLinkOff] = useState(false);
  // "copied" | "failed" | null — set when the box is ticked, since the whole
  // point of the flow is walking away with the URL.
  const [copyState, setCopyState] = useState(null);

  const baseline = deriveInitialSelection({ shares, peopleCandidates: people });
  const orphans = orphanGrants({ shares, teamCandidates: teams, peopleCandidates: people });

  const orgSelected = orgOverride === null ? baseline.orgSelected : orgOverride;
  const linkSelected = linkOverride === null ? baseline.linkSelected : linkOverride;
  const isTeamSelected = (slug) =>
    slug in teamOverrides ? teamOverrides[slug] : baseline.selectedTeamSlugs.has(slug);
  const isPersonSelected = (userId) =>
    userId in peopleOverrides ? peopleOverrides[userId] : baseline.selectedPeopleIds.has(userId);

  // Every team/person with a checkbox on screen — candidates PLUS orphans. This
  // is the set the save submits from, which is what makes "checked means access"
  // exactly true: there is no grant outside it.
  const allTeamSlugs = [...teams.map((t) => t.slug), ...orphans.teams.map((t) => t.slug)];
  const allPeopleIds = [...people.map((p) => p.userId), ...orphans.people.map((p) => p.userId)];
  const selectedTeamSlugs = new Set(allTeamSlugs.filter(isTeamSelected));
  const selectedPeopleIds = new Set(allPeopleIds.filter(isPersonSelected));

  const unresolvedCount = baseline.unresolvedUserShares.length;
  const canSave = unresolvedCount === 0 && !saving && !loading;

  function tickLinkOn() {
    setLinkOverride(true);
    // No clipboard write here any more: the URL box below appears with its own COPY
    // LINK button, so the copy happens when the owner asks for it. What waits for
    // Save is the GRANT, which the footer says plainly.
    setCopyState(null);
  }

  async function copyLink() {
    setCopyState(await copyForecastUrl(forecastUrl, writeClipboard));
  }

  function untickLinkConfirmed() {
    setLinkOverride(false);
    setConfirmingLinkOff(false);
    setCopyState(null);
  }

  function toggleTeam(slug) {
    setTeamOverrides((prev) => ({ ...prev, [slug]: !isTeamSelected(slug) }));
  }
  function togglePerson(userId) {
    setPeopleOverrides((prev) => ({ ...prev, [userId]: !isPersonSelected(userId) }));
  }

  async function handleSave() {
    if (!canSave) return;
    const desired = buildDesiredShares({
      orgSelected,
      // The save is a whole-set replace, so an omitted lane is a REVOKE, not a
      // no-op: leaving this out both refused to grant a newly ticked box and
      // would have dropped a loaded link grant on any unrelated edit.
      linkSelected,
      orgId,
      selectedTeamSlugs,
      selectedPeopleIds,
    });
    const ok = await onSave(desired);
    if (ok) onClose();
  }

  const addedTeams = [
    ...teams.filter((t) => selectedTeamSlugs.has(t.slug)).map((t) => ({ ...t, orphan: false })),
    ...orphans.teams
      .filter((t) => selectedTeamSlugs.has(t.slug))
      .map((t) => ({ ...t, orphan: true })),
  ];
  const addedPeople = [
    ...people.filter((p) => selectedPeopleIds.has(p.userId)).map((p) => ({ ...p, orphan: false })),
    ...orphans.people
      .filter((p) => selectedPeopleIds.has(p.userId))
      .map((p) => ({ ...p, orphan: true })),
  ];
  const directCount = addedTeams.length + addedPeople.length;

  // SEARCH RESULTS: only what is NOT already added, only once something is typed.
  // A removed orphan grant is offered here too, so a REMOVE can be undone before saving.
  const searching = query.trim() !== "";
  const resultTeams = [
    ...teams.filter((t) => !selectedTeamSlugs.has(t.slug) && matchesQuery(t.slug, query)),
    ...orphans.teams
      .filter((t) => !selectedTeamSlugs.has(t.slug) && matchesQuery(t.slug, query))
      .map((t) => ({ ...t, orphan: true })),
  ];
  const resultPeople = [
    ...people.filter(
      (p) => !selectedPeopleIds.has(p.userId) && matchesQuery(candidateLabel(p), query),
    ),
    ...orphans.people
      .filter((p) => !selectedPeopleIds.has(p.userId) && matchesQuery(p.label, query))
      .map((p) => ({ ...p, orphan: true })),
  ];
  const nothingMatches =
    !candidatesLoading && searching && resultTeams.length + resultPeople.length === 0;
  const noCandidates =
    !candidatesLoading &&
    teams.length === 0 &&
    people.length === 0 &&
    orphans.teams.length === 0 &&
    orphans.people.length === 0;
  const showResults = searchFocused && searching;

  const addTeam = (slug) => {
    toggleTeam(slug);
    setQuery("");
  };
  const addPerson = (userId) => {
    togglePerson(userId);
    setQuery("");
  };

  const removeButton = (kind, onClick) => (
    <Button
      variant="ghost"
      size="sm"
      data-share-remove=""
      {...ui("share.remove", kind)}
      onClick={onClick}
      style={{ flexShrink: 0 }}
    >
      Remove
    </Button>
  );

  return (
    <ModalShell
      onClose={onClose}
      surface="share"
      zIndex={500}
      // Names and emails in the access list are never recorded (services/interactionCapture.js).
      panelProps={{ "data-telemetry-redact": "" }}
      // Viewport-capped and scrolling, like the other modals, so "Update access" stays
      // reachable on a short screen.
      panelStyle={{
        width: "min(620px, 92vw)",
        maxHeight: "85vh",
        overflowY: "auto",
        padding: 0,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        boxShadow: C.shadowBox,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "24px 28px 0",
        }}
      >
        <div style={TITLE_STYLE}>Share this forecast</div>
        <CloseButton onClick={onClose} />
      </div>

      {/* A RECIPIENT GETS A SENTENCE, NOT A DISABLED PICKER. Showing them the
          controls greyed out would disclose who ELSE the forecast is shared with,
          which this feature deliberately withholds from viewers — a viewer must
          learn nothing about other grants. So the answer to "can I share this
          on?" is prose. */}
      {isOwn === false ? (
        <div data-share-viewer-notice="" style={{ ...HINT_STYLE, padding: "20px 28px 28px" }}>
          Only the forecast&rsquo;s owner can manage who it is shared with.
        </div>
      ) : loading ? (
        <LoadingLine style={{ padding: "20px 28px 28px" }}>Loading sharing settings…</LoadingLine>
      ) : (
        <>
          <div style={{ padding: "24px 28px 8px" }}>
            {/* ── PEOPLE AND TEAMS ───────────────────────────────────────────── */}
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={SECTION_STYLE}>People and teams</div>
              <div data-share-direct-count="" style={{ fontSize: 13, color: C.gray }}>
                {directCount} {directCount === 1 ? "has" : "have"} direct access
              </div>
            </div>

            <div data-share-list="" style={{ position: "relative", marginTop: 14 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "0 18px",
                  height: 52,
                  background: C.inputBg,
                  border: `1px solid ${showResults ? C.accent : C.border}`,
                  borderRadius: 10,
                }}
              >
                <SearchIcon />
                <input
                  type="text"
                  data-share-search=""
                  {...ui("share.search")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape" && query) {
                      e.stopPropagation();
                      setQuery("");
                    }
                  }}
                  placeholder="Add people or teams"
                  aria-label="Add people or teams"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    color: C.white,
                    fontSize: 15,
                  }}
                />
              </div>

              {/* Results float over the list below rather than pushing it down, and
                  close when the field loses focus. Each row takes the click on
                  mousedown (preventDefault) so the field's blur cannot close the
                  list before the click lands. */}
              {showResults && (
                <div
                  data-share-results=""
                  className="share-scroll"
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 58,
                    zIndex: 5,
                    maxHeight: ROW_HEIGHT * 4,
                    overflowY: "auto",
                    background: C.panel,
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    boxShadow: C.shadowBox,
                    padding: "6px 0",
                  }}
                >
                  {candidatesLoading ? (
                    <LoadingLine style={{ padding: "10px 18px" }}>
                      Loading people and teams…
                    </LoadingLine>
                  ) : nothingMatches ? (
                    <div style={{ ...HINT_STYLE, padding: "10px 18px" }}>
                      {noCandidates
                        ? "Colleagues appear here after they sign in; teams appear after their first member signs in."
                        : "No matches."}
                    </div>
                  ) : (
                    <>
                      {resultTeams.map((t) => (
                        <div
                          key={`team-${t.slug}`}
                          role="button"
                          tabIndex={-1}
                          data-share-result-team={t.slug}
                          {...ui("share.add-team")}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            addTeam(t.slug);
                          }}
                          className="share-result"
                          style={{ cursor: "pointer" }}
                        >
                          <EntryRow
                            team
                            title={t.slug}
                            sub={t.orphan ? orphanNote("Team · no current members") : teamSub(t)}
                            height={56}
                          />
                        </div>
                      ))}
                      {resultPeople.map((p) => (
                        <div
                          key={`person-${p.userId}`}
                          role="button"
                          tabIndex={-1}
                          data-share-result-person={p.userId}
                          {...ui("share.add-person")}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            addPerson(p.userId);
                          }}
                          className="share-result"
                          style={{ cursor: "pointer" }}
                        >
                          <EntryRow
                            title={p.orphan ? p.label : candidateLabel(p)}
                            sub={
                              p.orphan ? orphanNote("no longer in your organization") : personSub(p)
                            }
                            height={56}
                          />
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* WHO HAS DIRECT ACCESS — scrolls past three rows. */}
              <div
                data-share-added=""
                className="share-scroll"
                style={{
                  marginTop: 12,
                  maxHeight: ROW_HEIGHT * MAX_VISIBLE_ROWS,
                  overflowY: "auto",
                }}
              >
                {candidatesLoading ? (
                  // Not the rows yet: until the candidates arrive, a shared team would
                  // read as an orphan ("no current members") and a person by bare id.
                  <LoadingLine style={{ padding: "6px 2px" }}>
                    Loading people and teams…
                  </LoadingLine>
                ) : directCount === 0 ? (
                  <div style={{ ...HINT_STYLE, padding: "6px 2px" }}>
                    {noCandidates
                      ? "Colleagues appear here after they sign in; teams appear after their first member signs in."
                      : "No one has been added yet. Search above to add people or teams."}
                  </div>
                ) : (
                  <>
                    {addedTeams.map((t, i) => (
                      <div
                        key={`added-team-${t.slug}`}
                        style={{ borderTop: i > 0 ? `1px solid ${C.border}` : "none" }}
                      >
                        <EntryRow
                          team
                          title={t.slug}
                          sub={t.orphan ? orphanNote("Team · no current members") : teamSub(t)}
                          height={ROW_HEIGHT}
                          rowProps={{
                            "data-share-team": t.slug,
                            ...(t.orphan ? { "data-share-orphan": "" } : {}),
                          }}
                          trailing={removeButton("team", () => toggleTeam(t.slug))}
                        />
                      </div>
                    ))}
                    {addedPeople.map((p, i) => (
                      <div
                        key={`added-person-${p.userId}`}
                        style={{
                          borderTop: addedTeams.length + i > 0 ? `1px solid ${C.border}` : "none",
                        }}
                      >
                        <EntryRow
                          title={p.orphan ? p.label : candidateLabel(p)}
                          sub={
                            p.orphan ? orphanNote("no longer in your organization") : personSub(p)
                          }
                          height={ROW_HEIGHT}
                          rowProps={{
                            "data-share-person": p.userId,
                            ...(p.orphan ? { "data-share-orphan": "" } : {}),
                          }}
                          trailing={removeButton("person", () => togglePerson(p.userId))}
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* ── GENERAL ACCESS ─────────────────────────────────────────────── */}
            <div style={{ ...SECTION_STYLE, marginTop: 32 }}>General access</div>

            {/* ORG — a checkbox, not a scope. It is DYNAMIC: it grants to the org
                itself, so people who join later are included. That is why it is
                labelled as the organization rather than as a number of people; a
                snapshot labelled as the org is how an owner ends up believing they
                shared with everyone when they did not. */}
            <AccessCard
              data-share-org=""
              {...ui("share.org-access")}
              checked={orgSelected}
              disabled={!orgId}
              onChange={() => setOrgOverride(!orgSelected)}
              title={`Anyone at ${orgName || "your organization"}`}
              body={
                orgId
                  ? "Everyone in your organization, including future members."
                  : "You are not in an organization."
              }
            />

            {/* THE LINK. TICKING IS ADDITIVE AND UNDOABLE, so it just happens. UNTICKING
                revokes everyone the link reached, including the people who claimed it
                by opening it, so it asks first. */}
            <AccessCard
              data-share-link=""
              {...ui("share.link-access")}
              checked={linkSelected}
              disabled={!forecastUrl}
              onChange={() => (linkSelected ? setConfirmingLinkOff(true) : tickLinkOn())}
              title="Anyone with the link"
              body={
                forecastUrl
                  ? `Any signed-in Sayer user can open it, including people outside ${orgName || "your organization"}.`
                  : "Available once this forecast is saved."
              }
            >
              {confirmingLinkOff && (
                <div
                  data-share-link-confirm=""
                  style={{
                    marginTop: 14,
                    padding: 12,
                    border: `1px solid ${C.redTeam}`,
                    borderRadius: 8,
                  }}
                >
                  <div style={{ ...HINT_STYLE, marginBottom: 10 }}>
                    Revoke access for everyone who has this link? People who already opened it will
                    lose access too. This does not affect anyone you shared with by name.
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      variant="danger"
                      size="sm"
                      data-share-link-confirm-yes=""
                      {...ui("share.link-revoke-confirm")}
                      onClick={untickLinkConfirmed}
                    >
                      Turn off link
                    </Button>
                    <Button
                      size="sm"
                      data-share-link-confirm-no=""
                      {...ui("share.link-revoke-keep")}
                      onClick={() => setConfirmingLinkOff(false)}
                    >
                      Keep link
                    </Button>
                  </div>
                </div>
              )}
            </AccessCard>

            {linkSelected && forecastUrl && (
              <div style={{ display: "flex", gap: 12, marginTop: 14 }}>
                <input
                  type="text"
                  readOnly
                  data-share-link-url=""
                  {...ui("share.link-url")}
                  value={forecastUrl}
                  onFocus={(e) => e.target.select()}
                  aria-label="Forecast link"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: 40,
                    boxSizing: "border-box",
                    padding: "0 14px",
                    background: C.inputBg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    color: C.grayL,
                    fontFamily: MONO,
                    fontSize: 13,
                  }}
                />
                <Button
                  data-share-link-copy=""
                  {...ui("share.link-copy")}
                  onClick={copyLink}
                  style={{ flexShrink: 0 }}
                >
                  {copyState === "copied" ? "Copied" : "Copy link"}
                </Button>
              </div>
            )}
            {copyState === "failed" &&
              linkSelected && (
                // A refused clipboard says so instead of quietly claiming a copy; the
                // URL is selectable in the box above so the flow does not dead-end.
                <div data-share-link-copied="" style={{ ...HINT_STYLE, marginTop: 8 }}>
                  Couldn&rsquo;t copy automatically — select the link above and copy it.
                </div>
              )}

            {unresolvedCount > 0 && (
              <div
                data-share-unresolved=""
                style={{ ...HINT_STYLE, color: C.redTeam, marginTop: 14 }}
              >
                Sayer couldn&rsquo;t identify {unresolvedCount} of the people this is already shared
                with, so changes can&rsquo;t be saved right now. Saving could remove the wrong
                person. Try again later.
              </div>
            )}
            {error && <div style={{ ...HINT_STYLE, color: C.redTeam, marginTop: 14 }}>{error}</div>}
          </div>

          {/* FOOTER. Everything above commits together on UPDATE ACCESS — the PUT
              replaces the whole share set. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 20,
              padding: "20px 28px 24px",
              borderTop: `1px solid ${C.border}`,
            }}
          >
            <div style={{ ...HINT_STYLE, flex: 1 }}>Changes apply when you update access.</div>
            <Button {...ui("share.cancel")} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              data-share-save=""
              {...ui("share.save")}
              disabled={!canSave}
              onClick={handleSave}
              icon={saving ? <Spinner size="sm" label="Saving" /> : null}
            >
              {saving ? "Saving…" : "Update access"}
            </Button>
          </div>
        </>
      )}
    </ModalShell>
  );
}
